import { type AirHandlerUnit, type BoilerRoom, type Module } from "@eisox/api/core-api";

// Deep import: the feature barrel pulls in the AHU components, which import back from this feature.
import { getAirHandlerUnitsByGroup } from "~/features/air-handler-unit/helpers/group.helper";
import { m } from "~/paraglide/messages";

export const concatModuleNames = (
  moduleId: string,
  boilerRooms: BoilerRoom[],
  airHandlerUnits: AirHandlerUnit[],
): string | undefined => {
  const boilerRoomModule = boilerRooms.filter(m => m.moduleId === moduleId);
  const airHandlerUnitModules = airHandlerUnits.filter(ahu => ahu.moduleId === moduleId);

  const boilerRoomNames = boilerRoomModule
    .map(br => br.name)
    // oxlint-disable-next-line typescript/no-unnecessary-condition -- runtime API guard despite OpenAPI typing
    .filter(name => name !== undefined)
    .sort((a, b) => a.localeCompare(b));

  // Group air handler units and get display names (group name or individual name)
  const ahusByGroup = getAirHandlerUnitsByGroup(airHandlerUnitModules);
  const airHandlerUnitNames = Object.entries(ahusByGroup)
    .map(([key, ahus]) => {
      // If the first AHU has a group, use the group name (which is the key)
      // Otherwise, the key is the AHU's id, so use the AHU's name
      const firstAhu = ahus[0];
      if (!firstAhu) return undefined;
      return firstAhu.group ? key : firstAhu.name;
    })
    .filter((name): name is string => name !== undefined)
    .sort((a, b) => a.localeCompare(b));

  const allNames = [...boilerRoomNames, ...airHandlerUnitNames];

  return allNames.length > 0 ? allNames.join(", ") : undefined;
};

export const getPLCPageLink = (token: string, buildingId: string, moduleId: string): string =>
  `${import.meta.env.VITE_GTB_URL}/eisox/user/login?token=${token}&houseId=${buildingId}&moduleId=${moduleId}`;

/** Module display label: GTB name → boiler-room/AHU names → IP → BACnet device ID → "-". */
export const getModuleLabel = (module: Module, boilerRooms: BoilerRoom[], airHandlerUnits: AirHandlerUnit[]): string =>
  (module.proxy?.isEnabled ? module.proxy.name : undefined) ||
  concatModuleNames(module.id, boilerRooms, airHandlerUnits) ||
  module.url ||
  (module.bacnetDeviceId != null ? m.module_bacnet_device_id({ id: module.bacnetDeviceId }) : "-");

/** One option per module driving the given equipments, labeled with the names of the equipments it drives. */
export const getEquipmentModuleOptions = (
  equipments: Pick<BoilerRoom | AirHandlerUnit, "moduleId" | "name">[],
): { value: string; name: string }[] =>
  [...new Set(equipments.map(equipment => equipment.moduleId))].map(moduleId => ({
    value: moduleId,
    name: equipments
      .filter(equipment => equipment.moduleId === moduleId)
      .map(equipment => equipment.name)
      .join(", "),
  }));
